interface NumberFieldProps {
  label: string;
  value: number | undefined;
  onChange: (n: number) => void;
  step?: number;
  min?: number;
  max?: number;
  hint?: string;
  suffix?: string;
}
export function NumberField({
  label,
  value,
  onChange,
  step = 0.01,
  min,
  max,
  hint,
  suffix,
}: NumberFieldProps) {
  return (
    <label className="block text-sm">
      <div className="mb-1 font-medium text-slate-700">{label}</div>
      <div className="flex items-center">
        <input
          type="number"
          step={step}
          min={min}
          max={max}
          value={Number.isFinite(value as number) ? (value as number) : ''}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="w-full rounded-l border border-slate-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
        />
        {suffix && (
          <span className="rounded-r border border-l-0 border-slate-300 bg-slate-50 px-2 py-1.5 text-xs text-slate-500">
            {suffix}
          </span>
        )}
      </div>
      {hint && <p className="mt-1 text-xs text-slate-500">{hint}</p>}
    </label>
  );
}

interface ToggleFieldProps {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  hint?: string;
}
export function ToggleField({ label, value, onChange, hint }: ToggleFieldProps) {
  return (
    <label className="flex items-start justify-between gap-4 py-2">
      <div>
        <div className="text-sm font-medium text-slate-700">{label}</div>
        {hint && <p className="text-xs text-slate-500">{hint}</p>}
      </div>
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-5 w-5"
      />
    </label>
  );
}

interface SaveBarProps {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  onReset: () => void;
  warning?: string;
}
export function SaveBar({ dirty, saving, onSave, onReset, warning }: SaveBarProps) {
  if (!dirty) return null;
  return (
    <div className="sticky bottom-0 mt-6 flex items-center justify-between rounded-md border border-amber-300 bg-amber-50 px-4 py-3 shadow-sm">
      <div className="text-sm text-amber-900">
        {warning || 'You have unsaved changes.'}
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={onReset}
          disabled={saving}
          className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50"
        >
          Discard
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          className="rounded bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}

export function Card({
  title,
  children,
}: {
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      {title && (
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
          {title}
        </h2>
      )}
      {children}
    </section>
  );
}

/**
 * Shown above config forms when the underlying Firestore doc doesn't exist yet.
 * Reminds operators to seed defaults so values aren't blank.
 */
export function MissingConfigBanner({ docIds }: { docIds: string[] }) {
  return (
    <div className="mb-6 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <strong>Heads up:</strong> the underlying Firestore document
      {docIds.length > 1 ? 's' : ''}{' '}
      {docIds.map((d, i) => (
        <span key={d}>
          {i > 0 && ', '}
          <code className="rounded bg-amber-100 px-1">config/{d}</code>
        </span>
      ))}{' '}
      do{docIds.length > 1 ? '' : 'es'}n't exist yet, so all fields below show
      as blank. Run <code>node scripts/seed_runtime_configs.js</code> from the
      repo root to populate sensible defaults, then refresh this page. Saving
      from this form will create the document with whatever values you enter.
    </div>
  );
}
